import React, { useContext, useEffect } from 'react';
import {
  GLOBAL_EVENT_TYPES,
  NATIVE_TRIGGERS,
  ProjectInteraction,
  VevTriggersNative,
  VevTriggerType,
} from '@vev/utils';
import { useEditorState, useGlobalStore } from '../hooks';
import { dispatchVevEvent } from '..';
import { EventBrokerContext } from '../../components/vev-event/event-broker-context';

/**
 * `useInteractions` watches and returns all trigger interactions for a given key
 */
export function useInteractionTriggers(widgetKey: string): ProjectInteraction[] {
  const interactions = useGlobalStore((state) => state.interactions) || [];
  return interactions.filter((i) => i.trigger?.contentKey === widgetKey);
}

/**
 * `useInteractions` watches and returns all event interactions for a given key
 */
export function useInteractionEvents(widgetKey: string): ProjectInteraction[] {
  const interactions = useGlobalStore((state) => state.interactions) || [];
  return interactions.filter((i) => i.event?.contentKey === widgetKey);
}

function isNativeEvent(value: string): value is VevTriggersNative {
  return NATIVE_TRIGGERS.includes(value as VevTriggersNative);
}

type Return = {
  [key in VevTriggerType]?: () => void;
};

export const useInteractionNativeEvents = (
  key: string,
  show: boolean | undefined,
  setHide: (value: boolean | undefined) => void,
) => {
  const editor = useEditorState();
  const { addCallback, removeCallback } = useContext(EventBrokerContext);
  const interactionEvents = useInteractionEvents(key).filter((interaction) =>
    Object.values(GLOBAL_EVENT_TYPES).includes(interaction.event?.type as GLOBAL_EVENT_TYPES),
  );

  useEffect(() => {
    if (editor.disabled) return;

    function setVisibility(type?: string, show?: boolean) {
      switch (type) {
        case GLOBAL_EVENT_TYPES.SHOW:
          return false;
        case GLOBAL_EVENT_TYPES.HIDE:
          return true;
        case GLOBAL_EVENT_TYPES.TOGGLE:
          return show;
        default:
          return undefined;
      }
    }

    interactionEvents?.forEach(
      (interaction) => {
        addCallback(interaction.event?.type || '', key, () => {
          setHide(setVisibility(interaction.event?.type, show));
        });
      },
      [interactionEvents],
    );

    return () => {
      interactionEvents.forEach((interaction) => {
        if (interaction.event?.type) {
          removeCallback(interaction.event?.type, key);
        }
      });
    };
  }, [addCallback, interactionEvents, key, setHide, editor.disabled, removeCallback, show]);
};

export const useInteractionListeners = (key: string): Return => {
  const interactionTriggers = useInteractionTriggers(key);
  const [nativeTriggers, setNativeTriggers] = React.useState<VevTriggersNative[]>([]);

  const onTrigger = (type: VevTriggerType) => {
    dispatchVevEvent({
      type,
      contentKey: key,
    });
  };

  interactionTriggers?.forEach((interaction) => {
    const { type } = interaction.trigger || {};

    if (type && isNativeEvent(type)) {
      // Check if event has already been attached
      if (nativeTriggers.includes(type)) return;

      setNativeTriggers((triggers) => [...triggers, type]);
    }
  });

  const triggers: Return = {
    onVisible: () => onTrigger('onVisible'),
    onLeave: () => onTrigger('onLeave'),
  };

  for (const trigger of nativeTriggers) {
    triggers[trigger] = () => onTrigger(trigger);
  }

  return triggers;
};
